import {
  FormControl,
  FormControlLabel as MuiFormControlLabel,
  FormLabel as MuiFormLabel,
  Radio as MuiRadio,
  RadioGroup as MuiRadioGroup,
} from '@material-ui/core';
import { VisuallyHidden } from '@monorepo/shared/componentsV2/VisuallyHidden';
import { NullableString } from 'mapistry-shared';
import React, { useCallback } from 'react';
import styled, { css } from 'styled-components';
import { bodyMedium, bodyRegular } from '../styles/text';
import { SelectableOption } from '../types/SelectableOption';
import { Button } from './Button';
import { ConditionalWrapper } from './ConditionalWrapper';
import { ErrorText, SubText } from './styled/form';
import { Tooltip } from './Tooltip';

export type RadioGroupOption = Omit<SelectableOption, 'label'> & {
  label: string | React.ReactNode;
  tooltip?: string;
};

// label above the options
const FormLabel = styled(MuiFormLabel)`
  ${bodyMedium}
  color: ${({ theme }) => theme.colors.gray333};
  transform: none;

  &.Mui-focused {
    color: ${({ theme }) => theme.colors.gray333};
  }

  &.Mui-error {
    color: ${({ theme }) => theme.colors.darkRed};
  }
` as typeof MuiFormLabel;

// text next to options
const FormControlLabel = styled(MuiFormControlLabel)<{
  $isChecked: boolean;
  $variant?: RadioGroupVariant;
  $disabled?: boolean;
}>`
  & .MuiFormControlLabel-label {
    ${bodyRegular}
    ${({ $variant, $isChecked, $disabled }) =>
      $variant === 'outlined' &&
      css`
        width: 100%;
        padding: 0.5rem 1rem;
        margin: 0.25rem 0;
        border-radius: 8px;
        background-color: ${({ theme }) =>
          $isChecked ? `${theme.colors.builderBlue12}` : 'transparent'};
        outline-style: solid;
        outline-width: ${() => ($isChecked ? '2px' : '1px')};
        outline-color: ${({ theme }) => {
          if ($isChecked) {
            return theme.colors.builderBlue;
          }
          return $disabled ? theme.colors.grayddd : theme.colors.gray999;
        }};
      `}
  }
`;

/** Flex column so the label stretches to full width inside the RadioGroup. */
const TooltipChild = styled.span`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
const StyledSuperTitle = styled.div<{ $disabled?: boolean }>`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme, $disabled }) =>
    $disabled ? theme.colors.gray999 : theme.colors.gray666};
`;

const Radio = styled(MuiRadio)<{ $isCompact: boolean }>`
  /* less space between items vertically than the default MUI styles 9px,
     also keeping padding the same around the icon for on-hover highlight being perfectly round */
  padding: ${({ $isCompact }) => ($isCompact ? '6px' : '9px')};
  margin: ${({ $isCompact }) => ($isCompact ? '0 3px' : '0')};

  /* override MUI's coloring of the radio buttons */
  & .MuiSvgIcon-root {
    fill: ${({ theme }) => theme.colors.gray666};
  }

  &.Mui-checked .MuiSvgIcon-root {
    fill: ${({ theme }) => theme.colors.builderBlue};
  }

  /* we don't yet expose the ability to disable a radio button, but this is here for when we do -
     the opacity matches what MUI uses for disabled components */
  &.Mui-disabled .MuiSvgIcon-root {
    opacity: 0.38;
  }
`;

const ClearButton = styled(Button)`
  height: 1.25rem;
  margin: 0 0.25em;
  vertical-align: baseline;

  &:hover {
    background-color: transparent;
  }
`;

type RadioGroupVariant = 'outlined';

type RadioGroupProps = {
  className?: string;
  subText?: NullableString;
  error?: boolean;
  errorText?: string;
  isErrorTextHidden?: boolean;
  isLabelHidden?: boolean;
  isReadOnly?: boolean;
  label: string;
  name: string;
  onChange: React.ChangeEventHandler;
  options: RadioGroupOption[];
  required?: boolean;
  row?: boolean;
  value?: string;
  variant?: RadioGroupVariant;
  isSubtextHidden?: boolean;
};

export function RadioGroup({
  className,
  error,
  errorText,
  subText,
  isLabelHidden = false,
  label,
  name,
  onChange,
  options,
  required,
  row,
  value,
  variant,
  isReadOnly,
  isErrorTextHidden,
  isSubtextHidden,
}: RadioGroupProps) {
  const onClear = useCallback(() => {
    onChange({
      target: { name, value: '' },
    } as React.ChangeEvent<HTMLInputElement>);
  }, [onChange, name]);
  const labelComponent = (
    <FormLabel component="legend" required={required}>
      {label}
      {!required && (
        <ClearButton
          color="primary"
          type="button"
          onClick={onClear}
          disabled={!value || isReadOnly}
        >
          Clear
        </ClearButton>
      )}
    </FormLabel>
  );
  return (
    <FormControl component="fieldset" error={error} className={className}>
      {isLabelHidden ? (
        <VisuallyHidden>{labelComponent}</VisuallyHidden>
      ) : (
        labelComponent
      )}
      <MuiRadioGroup name={name} value={value} onChange={onChange} row={row}>
        {options.map((opt) => {
          const item = (
            <FormControlLabel
              $isChecked={value === opt.value}
              $variant={variant}
              $disabled={opt.disabled}
              value={opt.value}
              /* although we override the radio button coloring with our styling above, we set the color here
                to get gray for hover styling */
              control={
                <Radio required={required} color="default" $isCompact={!row} />
              }
              label={
                <>
                  {opt.supertitle && (
                    <StyledSuperTitle $disabled={opt.disabled}>
                      {opt.supertitle}
                    </StyledSuperTitle>
                  )}
                  {opt.label}
                </>
              }
              disabled={opt.disabled || isReadOnly}
            />
          );
          if (opt.tooltip) {
            return (
              <Tooltip
                title={opt.tooltip}
                key={opt.value}
                tabIndex={opt.disabled ? 0 : undefined}
              >
                <TooltipChild>{item}</TooltipChild>
              </Tooltip>
            );
          }
          return <React.Fragment key={opt.value}>{item}</React.Fragment>;
        })}
      </MuiRadioGroup>
      {subText && !isSubtextHidden && <SubText>{subText}</SubText>}
      <ConditionalWrapper
        condition={!!isErrorTextHidden}
        /* eslint-disable-next-line react/no-unstable-nested-components */
        wrapper={(children) => <VisuallyHidden>{children}</VisuallyHidden>}
      >
        <ErrorText>{errorText || ' '}</ErrorText>
      </ConditionalWrapper>
    </FormControl>
  );
}
